/**
 * DailyCheckinModal - Extracted from App.js Phase 3
 * MIQ Companion daily check-in form (v1.1 Verified Source Weighting)
 *
 * v1.2 — Auto-prefill from /api/health-sync/latest when modal opens.
 * If a wearable already synced today's sleep / steps / weight, prefill the form
 * and mark sleep_source / steps_source = 'wearable' automatically. User can override.
 */
import React, { useEffect, useRef } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// XHR helper bypasses fetch interceptor (consistent with codebase)
const xhrGet = (url) => new Promise((resolve, reject) => {
  const xhr = new XMLHttpRequest();
  xhr.open('GET', url, true);
  xhr.responseType = 'json';
  xhr.onload = () => (xhr.status >= 200 && xhr.status < 300) ? resolve(xhr.response) : reject(new Error(`HTTP ${xhr.status}`));
  xhr.onerror = () => reject(new Error('Network error'));
  xhr.send();
});

const DailyCheckinModal = ({
  isOpen,
  onClose,
  userId,
  checkinFormData,
  setCheckinFormData,
  checkedInToday,
  dailyCheckinContribution,
  dailyCheckinLoading,
  submitDailyCheckin,
  weightCheckStatus
}) => {
  // ─── Auto-prefill from health-sync on open ────────────────────────────
  const prefilledRef = useRef(false);
  useEffect(() => {
    if (!isOpen) { prefilledRef.current = false; return; }
    if (prefilledRef.current || !userId) return;
    prefilledRef.current = true;

    xhrGet(`${BACKEND_URL}/api/health-sync/latest/${userId}`)
      .then((res) => {
        const readings = res?.readings || {};
        const updates = {};
        const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
        const isFromToday = (reading) => {
          const ts = reading?.timestamp || reading?.synced_at;
          if (!ts) return false;
          return String(ts).slice(0, 10) === today;
        };

        // Sleep — value stored in minutes, UI splits hours/mins
        // Only prefill if reading is from today (last night's sleep counts as today's date)
        if (readings.sleep && isFromToday(readings.sleep) && !checkinFormData.sleep_hours && !checkinFormData.sleep_mins) {
          const totalMin = Number(readings.sleep.value) || 0;
          updates.sleep_hours = String(Math.floor(totalMin / 60));
          updates.sleep_mins = String(totalMin % 60);
          updates.sleep_source = 'wearable';
          updates._sleep_synced_from = readings.sleep.source;
        }

        // Steps — only prefill if reading is from today
        if (readings.steps && isFromToday(readings.steps) && !checkinFormData.steps_count) {
          updates.steps_count = String(Math.round(Number(readings.steps.value) || 0));
          updates.steps_source = 'wearable';
          updates._steps_synced_from = readings.steps.source;
        }

        // Weight (only if weight check needed and field empty) — only today's reading
        if (readings.weight && isFromToday(readings.weight) && !checkinFormData.weight && weightCheckStatus?.needs_weight_entry) {
          updates.weight = String(Number(readings.weight.value).toFixed(1));
          updates._weight_synced_from = readings.weight.source;
        }

        if (Object.keys(updates).length > 0) {
          setCheckinFormData((prev) => ({ ...prev, ...updates }));
        }
      })
      .catch(() => { /* silent — empty data is normal */ });
  }, [isOpen, userId, weightCheckStatus, checkinFormData, setCheckinFormData]);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '420px', maxHeight: '90vh', overflowY: 'auto' }}>
        <h2 className="heading-3" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          Daily Check-In
          {checkedInToday && <span style={{ fontSize: '14px', color: '#38ef7d' }}>✓</span>}
        </h2>
        <p className="modal-description" style={{ marginBottom: '12px' }}>
          Daily check-ins improve MIQ Confidence. You can skip anytime.
        </p>
        
        {/* v1.1 - Verified Source Info */}
        <div style={{
          background: 'rgba(102, 126, 234, 0.1)',
          borderRadius: '8px',
          padding: '10px 12px',
          marginBottom: '16px',
          border: '1px solid rgba(102, 126, 234, 0.2)'
        }}>
          <p style={{ margin: 0, fontSize: '11px', color: '#667eea', fontWeight: '500' }}>
            Using data from a wearable or health app typically improves accuracy and increases your MIQ Confidence.
          </p>
        </div>
        
        {/* Daily Check-In Form */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
          
          {/* SLEEP SECTION */}
          <div>
            <label style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)', display: 'block', marginBottom: '6px' }}>
              1. Sleep Quality
              {checkinFormData._sleep_synced_from && (
                <span data-testid="sleep-synced-badge" style={{
                  marginLeft: '8px', padding: '2px 6px', background: 'rgba(16, 185, 129, 0.2)',
                  color: '#10b981', borderRadius: '8px', fontSize: '9px', fontWeight: 700
                }}>
                  SYNCED
                </span>
              )}
            </label>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '10px' }}>
              {[
                { value: 'poor', label: 'Poor', color: '#f44336' },
                { value: 'fair', label: 'Fair', color: '#ff9800' },
                { value: 'good', label: 'Good', color: '#4caf50' },
                { value: 'excellent', label: 'Excellent', color: '#38ef7d' }
              ].map(opt => (
                <button
                  key={opt.value}
                  data-testid={`sleep-quality-${opt.value}`}
                  onClick={() => setCheckinFormData({...checkinFormData, sleep_quality: opt.value})}
                  style={{
                    flex: '1 1 calc(25% - 6px)',
                    minWidth: '70px',
                    padding: '8px 6px',
                    borderRadius: '8px',
                    border: checkinFormData.sleep_quality === opt.value ? `2px solid ${opt.color}` : '1px solid rgba(255,255,255,0.15)',
                    background: checkinFormData.sleep_quality === opt.value ? `${opt.color}20` : 'rgba(255,255,255,0.05)',
                    color: checkinFormData.sleep_quality === opt.value ? opt.color : 'var(--text-secondary)',
                    fontSize: '11px',
                    fontWeight: checkinFormData.sleep_quality === opt.value ? '600' : '400',
                    cursor: 'pointer'
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            
            {/* Sleep Hours & Minutes Input */}
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
              <input
                type="number"
                placeholder="Hours"
                value={checkinFormData.sleep_hours || ''}
                onChange={(e) => setCheckinFormData({...checkinFormData, sleep_hours: e.target.value})}
                style={{ width: '70px', padding: '8px 10px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.05)', color: 'var(--text-primary)', fontSize: '12px' }}
                min="0" max="24"
              />
              <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>hrs</span>
              <input
                type="number"
                placeholder="Min"
                value={checkinFormData.sleep_mins || ''}
                onChange={(e) => setCheckinFormData({...checkinFormData, sleep_mins: e.target.value})}
                style={{ width: '60px', padding: '8px 10px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.05)', color: 'var(--text-primary)', fontSize: '12px' }}
                min="0" max="59"
              />
              <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>min</span>
            </div>
            
            {/* Sleep Data Source */}
            <div>
              <label style={{ fontSize: '10px', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>
                Sleep Data Source
              </label>
              <div style={{ display: 'flex', gap: '6px' }}>
                {[
                  { value: 'wearable', label: 'From wearable or health app' },
                  { value: 'self_report', label: 'My own estimate' }
                ].map(opt => (
                  <button
                    key={opt.value}
                    data-testid={`sleep-source-${opt.value}`}
                    onClick={() => setCheckinFormData({...checkinFormData, sleep_source: opt.value})}
                    style={{
                      flex: 1,
                      padding: '6px 8px',
                      borderRadius: '6px',
                      border: checkinFormData.sleep_source === opt.value ? '2px solid #667eea' : '1px solid rgba(255,255,255,0.15)',
                      background: checkinFormData.sleep_source === opt.value ? 'rgba(102, 126, 234, 0.15)' : 'rgba(255,255,255,0.03)',
                      color: checkinFormData.sleep_source === opt.value ? '#667eea' : 'var(--text-secondary)',
                      fontSize: '10px',
                      cursor: 'pointer'
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
          
          {/* STRESS SECTION */}
          <div>
            <label style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)', display: 'block', marginBottom: '6px' }}>
              2. Stress Level
            </label>
            <div style={{ display: 'flex', gap: '8px' }}>
              {[
                { value: 'low', label: 'Low', color: '#38ef7d' },
                { value: 'moderate', label: 'Moderate', color: '#ff9800' },
                { value: 'high', label: 'High', color: '#f44336' }
              ].map(opt => (
                <button
                  key={opt.value}
                  data-testid={`stress-level-${opt.value}`}
                  onClick={() => setCheckinFormData({...checkinFormData, stress_level: opt.value})}
                  style={{
                    flex: 1,
                    padding: '10px 8px',
                    borderRadius: '8px',
                    border: checkinFormData.stress_level === opt.value ? `2px solid ${opt.color}` : '1px solid rgba(255,255,255,0.15)',
                    background: checkinFormData.stress_level === opt.value ? `${opt.color}20` : 'rgba(255,255,255,0.05)',
                    color: checkinFormData.stress_level === opt.value ? opt.color : 'var(--text-secondary)',
                    fontSize: '11px',
                    fontWeight: checkinFormData.stress_level === opt.value ? '600' : '400',
                    cursor: 'pointer'
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          
          {/* ACTIVITY / STEPS SECTION */}
          <div>
            <label style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)', display: 'block', marginBottom: '6px' }}>
              3. Activity / Movement
              {checkinFormData._steps_synced_from && (
                <span data-testid="steps-synced-badge" style={{
                  marginLeft: '8px', padding: '2px 6px', background: 'rgba(16, 185, 129, 0.2)',
                  color: '#10b981', borderRadius: '8px', fontSize: '9px', fontWeight: 700
                }}>
                  SYNCED
                </span>
              )}
            </label>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
              {[
                { value: 'rest_light', label: 'Rest/Light', color: '#667eea' },
                { value: 'moderate', label: 'Moderate', color: '#4caf50' },
                { value: 'intense', label: 'Intense', color: '#ff9800' }
              ].map(opt => (
                <button
                  key={opt.value}
                  data-testid={`activity-level-${opt.value}`}
                  onClick={() => setCheckinFormData({...checkinFormData, activity_level: opt.value})}
                  style={{
                    flex: 1,
                    padding: '10px 8px',
                    borderRadius: '8px',
                    border: checkinFormData.activity_level === opt.value ? `2px solid ${opt.color}` : '1px solid rgba(255,255,255,0.15)',
                    background: checkinFormData.activity_level === opt.value ? `${opt.color}20` : 'rgba(255,255,255,0.05)',
                    color: checkinFormData.activity_level === opt.value ? opt.color : 'var(--text-secondary)',
                    fontSize: '11px',
                    fontWeight: checkinFormData.activity_level === opt.value ? '600' : '400',
                    cursor: 'pointer'
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            
            {/* Steps Count Input */}
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
              <input
                type="number"
                placeholder="Steps count"
                value={checkinFormData.steps_count}
                onChange={(e) => setCheckinFormData({...checkinFormData, steps_count: e.target.value})}
                style={{ flex: 1, padding: '8px 10px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.05)', color: 'var(--text-primary)', fontSize: '12px' }}
                min="0" max="100000"
              />
              <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>steps</span>
            </div>
            
            {/* Steps Data Source */}
            <div>
              <label style={{ fontSize: '10px', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>
                Steps Data Source
              </label>
              <div style={{ display: 'flex', gap: '6px' }}>
                {[
                  { value: 'wearable', label: 'From wearable or health app' },
                  { value: 'self_report', label: 'My own estimate' }
                ].map(opt => (
                  <button
                    key={opt.value}
                    data-testid={`steps-source-${opt.value}`}
                    onClick={() => setCheckinFormData({...checkinFormData, steps_source: opt.value})}
                    style={{
                      flex: 1,
                      padding: '6px 8px',
                      borderRadius: '6px',
                      border: checkinFormData.steps_source === opt.value ? '2px solid #667eea' : '1px solid rgba(255,255,255,0.15)',
                      background: checkinFormData.steps_source === opt.value ? 'rgba(102, 126, 234, 0.15)' : 'rgba(255,255,255,0.03)',
                      color: checkinFormData.steps_source === opt.value ? '#667eea' : 'var(--text-secondary)',
                      fontSize: '10px',
                      cursor: 'pointer'
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
          
          {/* WEEKLY WEIGHT CHECK-IN */}
          {weightCheckStatus?.needs_weight_entry && (
            <div style={{
              background: 'rgba(255, 152, 0, 0.08)',
              borderRadius: '10px',
              padding: '14px',
              border: '1px solid rgba(255, 152, 0, 0.25)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                <span style={{ fontSize: '16px' }}>⚖️</span>
                <div>
                  <p style={{ margin: 0, fontSize: '13px', fontWeight: '600', color: '#ff9800' }}>
                    Weekly Weight Check
                  </p>
                  <p style={{ margin: '2px 0 0 0', fontSize: '10px', color: 'var(--text-secondary)' }}>
                    {weightCheckStatus.prompt_message}
                  </p>
                </div>
              </div>
              
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input
                  type="number"
                  placeholder={weightCheckStatus.last_weight ? `Last: ${weightCheckStatus.last_weight}` : "Enter weight"}
                  value={checkinFormData.weight}
                  onChange={(e) => setCheckinFormData({...checkinFormData, weight: e.target.value})}
                  style={{
                    flex: 1, padding: '10px 12px', borderRadius: '8px',
                    border: '1px solid rgba(255, 152, 0, 0.3)', background: 'rgba(255, 152, 0, 0.05)',
                    color: 'var(--text-primary)', fontSize: '14px'
                  }}
                  step="0.1" min="20" max="500"
                />
                <select
                  value={checkinFormData.weight_unit}
                  onChange={(e) => setCheckinFormData({...checkinFormData, weight_unit: e.target.value})}
                  style={{
                    padding: '10px 8px', borderRadius: '8px',
                    border: '1px solid rgba(255, 152, 0, 0.3)', background: 'rgba(255, 152, 0, 0.05)',
                    color: 'var(--text-primary)', fontSize: '13px', cursor: 'pointer'
                  }}
                >
                  <option value="kg">kg</option>
                  <option value="lbs">lbs</option>
                </select>
              </div>
              
              <p style={{ margin: '8px 0 0 0', fontSize: '9px', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                Regular weight tracking helps MIQ adjust your goals automatically
              </p>
            </div>
          )}
          
          {/* OPTIONAL NOTES */}
          <div>
            <label style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)', display: 'block', marginBottom: '6px' }}>
              3. Notes (Optional)
            </label>
            <textarea
              data-testid="checkin-notes"
              placeholder="How did today feel? (Optional)"
              value={checkinFormData.notes}
              onChange={(e) => setCheckinFormData({...checkinFormData, notes: e.target.value})}
              style={{
                width: '100%', minHeight: '50px', padding: '8px 10px', borderRadius: '6px',
                border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.05)',
                color: 'var(--text-primary)', fontSize: '12px', resize: 'none'
              }}
              maxLength={200}
            />
            <p style={{ fontSize: '9px', color: 'var(--text-secondary)', marginTop: '2px', textAlign: 'right' }}>
              {checkinFormData.notes.length}/200
            </p>
          </div>
          
          {/* MIQ CONTRIBUTION INFO */}
          {dailyCheckinContribution && (
            <div style={{
              background: 'rgba(56, 239, 125, 0.08)',
              borderRadius: '8px',
              padding: '10px 12px',
              border: '1px solid rgba(56, 239, 125, 0.2)'
            }}>
              <p style={{ margin: '0 0 6px 0', fontSize: '12px', color: '#38ef7d', fontWeight: '600' }}>
                MIQ Confidence: +{dailyCheckinContribution.confidence_boost}% / 15%
              </p>
              <div style={{ fontSize: '10px', color: 'var(--text-secondary)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '2px' }}>
                  <span style={{ color: dailyCheckinContribution.checkin_completed ? '#38ef7d' : 'var(--text-secondary)' }}>
                    {dailyCheckinContribution.checkin_completed ? '✓' : '○'}
                  </span>
                  <span>Daily Check-ins Completed (+9%)</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '2px' }}>
                  <span style={{ color: dailyCheckinContribution.sleep_verified ? '#38ef7d' : 'var(--text-secondary)' }}>
                    {dailyCheckinContribution.sleep_verified ? '✓' : '○'}
                  </span>
                  <span>Sleep Source Verified (+3%)</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <span style={{ color: dailyCheckinContribution.steps_verified ? '#38ef7d' : 'var(--text-secondary)' }}>
                    {dailyCheckinContribution.steps_verified ? '✓' : '○'}
                  </span>
                  <span>Steps Source Verified (+3%)</span>
                </div>
              </div>
              {dailyCheckinContribution.consecutive_days > 1 && (
                <p style={{ margin: '6px 0 0 0', fontSize: '10px', color: '#ff9800' }}>
                  {dailyCheckinContribution.consecutive_days} day streak!
                </p>
              )}
            </div>
          )}
          
          {/* Buttons */}
          <div style={{ display: 'flex', gap: '10px', marginTop: '4px' }}>
            <button className="btn-secondary" onClick={onClose} style={{ flex: 1 }}>
              Skip
            </button>
            <button 
              className="btn-primary"
              data-testid="save-checkin-btn"
              onClick={async () => {
                const result = await submitDailyCheckin(checkinFormData);
                if (!result.success) {
                  alert(result.message);
                }
              }}
              disabled={dailyCheckinLoading}
              style={{ flex: 2, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
            >
              {dailyCheckinLoading ? 'Saving...' : (checkedInToday ? 'Update Check-In' : 'Save Check-In')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DailyCheckinModal;
